import type { TopicMetadata } from '../types';
import type { CurriculumPlacementKey } from './curriculumUtils';
import { getLeafTopics, getTopicId, isTenantCustomCatalogProvider } from './topicUtils';

/**
 * Max topic ids per C259/C310 command within one subject + key stage + year folder.
 * Fewer than this in a folder → one command; more → split into chunks of at most this size.
 */
export const ADD_TOPICS_MAX_PER_COMMAND = 150;

export type PlacementOverride = {
  keyStageId: string;
  yearId: string;
};

export type AddTopicsJob =
  | {
      type: 'c259';
      topicIds: string[];
      providerKey: string;
      sourceCatalogSubjectId: string;
      sourceKeyStageId: string;
      sourceYearId: string;
      placement: PlacementOverride | null;
    }
  | {
      type: 'c310';
      topicIds: string[];
      placement: PlacementOverride | null;
    };

export type ReviewTopicRow = {
  topic: any;
  topicId: string;
  name: string;
  breadcrumb: string;
};

export type ReviewYearSection = {
  keyStageId: string;
  keyStageName: string;
  yearId: string;
  yearLabel: string;
  topics: ReviewTopicRow[];
  folderKey: string;
};

export type SubjectReviewCard = {
  subjectId: string;
  subjectName: string;
  educationSystemKey: string;
  isOffendingEducationSystem: boolean;
  yearSections: ReviewYearSection[];
};

export type EducationSystemValidation = {
  isValid: boolean;
  /** Normalized keys present in selection (trimmed, lowercased for compare). */
  distinctKeys: string[];
  /** When invalid: ES key that appears most often. */
  dominantKey: string | null;
  /** When invalid: ES key(s) with the fewest topics — highlight these for removal. */
  minorityKeys: string[];
};

function normalizeEsKey(k: string | null | undefined): string {
  return (k && String(k).trim()) || '';
}

function normalizeEsCompare(k: string | null | undefined): string {
  return normalizeEsKey(k).toLowerCase();
}

export function chunkArray<T>(items: T[], size: number): T[][] {
  if (size <= 0) return [items];
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

export function validateSingleEducationSystem(
  leafTopics: any[],
  extractTopicMetadata: (topic: any) => TopicMetadata
): EducationSystemValidation {
  const keyCounts = new Map<string, number>();
  for (const t of leafTopics) {
    const meta = extractTopicMetadata(t);
    const k = normalizeEsCompare(meta.educationSystemKey);
    const bucket = k || '__missing__';
    keyCounts.set(bucket, (keyCounts.get(bucket) || 0) + 1);
  }
  const distinctKeys = [...keyCounts.keys()];
  if (distinctKeys.length <= 1) {
    const only = distinctKeys[0];
    return {
      isValid: true,
      distinctKeys: only && only !== '__missing__' ? [only] : [],
      dominantKey: only && only !== '__missing__' ? only : null,
      minorityKeys: []
    };
  }
  let dominantKey = distinctKeys[0];
  let max = 0;
  let minCount = Infinity;
  for (const [k, c] of keyCounts) {
    if (c > max) {
      max = c;
      dominantKey = k;
    }
    if (c < minCount) {
      minCount = c;
    }
  }
  const minorityKeys = [...keyCounts.entries()]
    .filter(([, c]) => c === minCount)
    .map(([k]) => (k === '__missing__' ? null : k))
    .filter((k): k is string => k != null);
  return {
    isValid: false,
    distinctKeys,
    dominantKey: dominantKey === '__missing__' ? null : dominantKey,
    minorityKeys
  };
}

function folderKey(meta: TopicMetadata, providerKey: string): string {
  return [
    providerKey,
    (meta.subjectId || '').trim(),
    (meta.keyStageId || '').trim(),
    (meta.yearId || '').trim()
  ].join('|');
}

export function buildSubjectReviewCards(
  leafTopics: any[],
  extractTopicMetadata: (topic: any) => TopicMetadata,
  buildBreadcrumb: (topic: any) => string,
  offendingEducationSystemKeys: ReadonlySet<string> | null
): SubjectReviewCard[] {
  const offendingSet =
    offendingEducationSystemKeys && offendingEducationSystemKeys.size > 0
      ? offendingEducationSystemKeys
      : null;

  const bySubject = new Map<
    string,
    {
      subjectName: string;
      educationSystemKey: string;
      sections: Map<string, ReviewYearSection>;
    }
  >();

  for (const topic of leafTopics) {
    const meta = extractTopicMetadata(topic);
    const subjectId = (meta.subjectId || '').trim() || 'unknown-subject';
    const subjectName = (meta.subjectName || 'Unknown subject').trim();
    const esKey = normalizeEsKey(meta.educationSystemKey);
    const providerKey =
      topic.e179_ProviderKey || topic.e180_ProviderKey || 'oak';
    const fk = folderKey(meta, providerKey);

    if (!bySubject.has(subjectId)) {
      bySubject.set(subjectId, {
        subjectName,
        educationSystemKey: esKey,
        sections: new Map()
      });
    }
    const subj = bySubject.get(subjectId)!;
    if (!subj.sections.has(fk)) {
      subj.sections.set(fk, {
        keyStageId: (meta.keyStageId || '').trim(),
        keyStageName: (meta.keyStageName || '').trim(),
        yearId: (meta.yearId || '').trim(),
        yearLabel: (meta.yearLabel || '').trim(),
        folderKey: fk,
        topics: []
      });
    }
    const topicId = getTopicId(topic) || '';
    subj.sections.get(fk)!.topics.push({
      topic,
      topicId,
      name: topic.e179_Name || topic.e180_Name || 'Topic',
      breadcrumb: buildBreadcrumb(topic)
    });
  }

  const cards: SubjectReviewCard[] = [];
  for (const [subjectId, data] of bySubject) {
    const esNorm = normalizeEsCompare(data.educationSystemKey);
    const isOffending =
      offendingSet != null && esNorm !== '' && offendingSet.has(esNorm);
    cards.push({
      subjectId,
      subjectName: data.subjectName,
      educationSystemKey: data.educationSystemKey,
      isOffendingEducationSystem: isOffending,
      yearSections: [...data.sections.values()].sort((a, b) => {
        const ak = `${a.keyStageName}|${a.yearLabel}`;
        const bk = `${b.keyStageName}|${b.yearLabel}`;
        return ak.localeCompare(bk);
      })
    });
  }

  return cards.sort((a, b) => a.subjectName.localeCompare(b.subjectName));
}

export function needsPlacementOverride(
  sourceEducationSystemKey: string | null | undefined,
  studentEducationSystemKey: string | null | undefined
): boolean {
  const src = normalizeEsCompare(sourceEducationSystemKey);
  const stu = normalizeEsCompare(studentEducationSystemKey);
  if (!src || !stu) return false;
  return src !== stu;
}

export function buildAddTopicsJobs(
  leafTopics: any[],
  extractTopicMetadata: (topic: any) => TopicMetadata,
  placementByFolderKey: Record<string, PlacementOverride | null | undefined>,
  studentEducationSystemKey: string,
  defaultBuiltInProviderKey: string
): AddTopicsJob[] {
  const byFolder = new Map<
    string,
    {
      meta: TopicMetadata;
      providerKey: string;
      topics: any[];
    }
  >();

  for (const topic of leafTopics) {
    const meta = extractTopicMetadata(topic);
    const providerKey =
      topic.e179_ProviderKey || topic.e180_ProviderKey || defaultBuiltInProviderKey;
    const fk = folderKey(meta, providerKey);
    if (!byFolder.has(fk)) {
      byFolder.set(fk, { meta, providerKey, topics: [] });
    }
    byFolder.get(fk)!.topics.push(topic);
  }

  const jobs: AddTopicsJob[] = [];

  for (const [fk, group] of byFolder) {
    const { meta, providerKey, topics } = group;
    const useCustom = isTenantCustomCatalogProvider(providerKey);
    const overrideNeeded = needsPlacementOverride(
      meta.educationSystemKey,
      studentEducationSystemKey
    );
    const placement = overrideNeeded ? placementByFolderKey[fk] ?? null : null;

    const topicIds = topics
      .map((t) => getTopicId(t))
      .filter((id): id is string => !!id && String(id).trim() !== '');

    for (const chunk of chunkArray(topicIds, ADD_TOPICS_MAX_PER_COMMAND)) {
      if (chunk.length === 0) continue;
      if (useCustom) {
        jobs.push({ type: 'c310', topicIds: chunk, placement });
      } else {
        jobs.push({
          type: 'c259',
          topicIds: chunk,
          providerKey,
          sourceCatalogSubjectId: (meta.subjectId || '').trim(),
          sourceKeyStageId: (meta.keyStageId || '').trim(),
          sourceYearId: (meta.yearId || '').trim(),
          placement
        });
      }
    }
  }

  return jobs;
}

export function selectionUsesMultipleProviders(leafTopics: any[]): boolean {
  let sawCustom = false;
  let sawBuiltIn = false;
  for (const t of leafTopics) {
    const pk = t.e179_ProviderKey || t.e180_ProviderKey;
    if (isTenantCustomCatalogProvider(pk)) sawCustom = true;
    else if (pk) sawBuiltIn = true;
    if (sawCustom && sawBuiltIn) return true;
  }
  return false;
}

export function prepareLeafTopicsForWizard(selectedTopics: any[]): any[] {
  return getLeafTopics(selectedTopics);
}

/** Step 1 review: auto-expand a lone subject card only when its selection is at most this size. */
export const REVIEW_SUBJECT_AUTO_EXPAND_MAX_TOPICS = 50;

export function topicCountForSubjectReviewCard(card: SubjectReviewCard): number {
  return card.yearSections.reduce((n, section) => n + section.topics.length, 0);
}

/** Subject ids to expand on wizard step 1 (collapsed when multiple subjects or >50 topics). */
export function initialExpandedSubjectIdsForReview(
  subjectCards: SubjectReviewCard[]
): Set<string> {
  if (subjectCards.length !== 1) return new Set();
  const card = subjectCards[0];
  if (!card) return new Set();
  if (topicCountForSubjectReviewCard(card) > REVIEW_SUBJECT_AUTO_EXPAND_MAX_TOPICS) {
    return new Set();
  }
  return new Set([card.subjectId]);
}

/** Destination KS/year in the student curriculum (respects placement overrides from the wizard). */
export function collectEffectivePlacementsForAddedTopics(
  leafTopics: any[],
  extractTopicMetadata: (topic: any) => TopicMetadata,
  placementByFolderKey: Record<string, PlacementOverride | null | undefined>,
  studentEducationSystemKey: string,
  defaultBuiltInProviderKey: string
): CurriculumPlacementKey[] {
  const seen = new Set<string>();
  const out: CurriculumPlacementKey[] = [];
  for (const topic of leafTopics) {
    const meta = extractTopicMetadata(topic);
    const providerKey =
      topic.e179_ProviderKey || topic.e180_ProviderKey || defaultBuiltInProviderKey;
    const fk = folderKey(meta, providerKey);
    const overrideNeeded = needsPlacementOverride(
      meta.educationSystemKey,
      studentEducationSystemKey
    );
    const keyStageId = overrideNeeded
      ? (placementByFolderKey[fk]?.keyStageId || '').trim()
      : (meta.keyStageId || '').trim();
    const yearId = overrideNeeded
      ? (placementByFolderKey[fk]?.yearId || '').trim()
      : (meta.yearId || '').trim();
    const subjectName = (meta.subjectName || '').trim();
    if (!keyStageId || !yearId) continue;
    const key = `${subjectName.toLowerCase()}|${keyStageId}|${yearId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      keyStageId,
      yearId,
      ...(subjectName ? { subjectName } : {})
    });
  }
  return out;
}

export type AddTopicsBatchProgressLabels = {
  headline: string;
  detail: string;
  showBar: boolean;
  percent: number;
};

/** User-facing copy for step 2 (batched add progress). */
export function describeAddTopicsBatchProgress(
  batchIndex: number,
  totalBatches: number,
  topicsAddedSoFar: number,
  totalTopics: number
): AddTopicsBatchProgressLabels {
  const batchNum = Math.min(batchIndex + 1, Math.max(totalBatches, 1));
  let headline: string;
  if (totalBatches > 1) {
    headline = `Batch ${batchNum} of ${totalBatches}`;
  } else {
    headline = `Adding ${totalTopics} topic${totalTopics === 1 ? '' : 's'}`;
  }
  const detail = `${topicsAddedSoFar} of ${totalTopics} topics added so far`;
  const showBar = totalBatches > 1;
  const percent =
    totalTopics > 0 ? Math.min(100, Math.round((topicsAddedSoFar / totalTopics) * 100)) : 0;
  return { headline, detail, showBar, percent };
}
